import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, Camera, Loader2 } from "lucide-react";
import AuthImage from "@/components/AuthImage";
import { toast } from "sonner";

/** Attach + preview photos for a given maintenance ticket. */
export default function MaintenancePhotos({ ticket, editable = true, onUpdate }) {
  const [photos, setPhotos] = useState(ticket.photos || []);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { setPhotos(ticket.photos || []); }, [ticket.id, ticket.photos]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/uploads/maintenance/${ticket.id}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      }
    }
    setPhotos((prev) => [...prev, ...added]);
    setUploading(false);
    e.target.value = "";
    if (added.length) toast.success(`${added.length} photo${added.length === 1 ? "" : "s"} added`);
    onUpdate?.();
  };

  return (
    <div className="mt-3" data-testid={`photos-${ticket.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500">Photos ({photos.length})</div>
        {editable && (
          <label
            htmlFor={`file-${ticket.id}`}
            className="cursor-pointer text-xs inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/10 hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5"
            data-testid={`upload-photo-${ticket.id}`}
          >
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
            {uploading ? "Uploading" : "Add photo"}
          </label>
        )}
        <input
          id={`file-${ticket.id}`}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          className="hidden"
          disabled={uploading}
        />
      </div>
      {photos.length === 0 ? (
        <div className="text-xs text-zinc-500 italic">No photos yet. Add one to help the vendor bid without a site visit.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {photos.map((p, i) => (
            <AuthImage
              key={p.path || i}
              path={p.path}
              alt=""
              className="w-full aspect-square rounded-md object-cover border border-black/10 dark:border-white/10"
            />
          ))}
        </div>
      )}
    </div>
  );
}
